import { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { User, Mail, Lock, Save, LogOut, ArrowLeft, Clock, Bell, MapPin, Navigation, Shield, AlertCircle, Smartphone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  isPushSupported, 
  getNotificationPermission, 
  requestNotificationPermission, 
  subscribeToPush,
  unsubscribeFromPush
} from '../utils/pushNotifications';


export default function DriverSettings() {
  const { user, token, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [preferencesLoading, setPreferencesLoading] = useState(false);
  const [emailSettingsLoading, setEmailSettingsLoading] = useState(false);
  const [notificationPrefsLoading, setNotificationPrefsLoading] = useState(false);
  const [emailData, setEmailData] = useState({
    new_email: user?.email || '',
    current_password: ''
  });
  
  const [preferences, setPreferences] = useState({
    time_format: user?.time_format || '24',
    date_format: user?.date_format || 'DD/MM/YYYY'
  });

  const [emailSettings, setEmailSettings] = useState({
    enabled: false,
    smtp_host: 'smtp.gmail.com',
    smtp_port: 587,
    smtp_user: '',
    smtp_from: '',
    smtp_password: ''
  });

  const [notificationPrefs, setNotificationPrefs] = useState({
    ride_assigned: true,
    ride_status_changed: true,
    email_ride_assigned: true,
    email_ride_status_changed: true,
    push_ride_assigned: true,
    push_ride_status_changed: true,
    reminder_30min_before: true  // 30 minutes before ride reminder
  });

  // GPS Permission State
  const [gpsPermission, setGpsPermission] = useState('unknown'); // 'granted', 'denied', 'prompt', 'unknown'
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsTrackingEnabled, setGpsTrackingEnabled] = useState(true); // App-level GPS tracking toggle
  const [showGPSInstructionsModal, setShowGPSInstructionsModal] = useState(false);
  const [detectedPlatform, setDetectedPlatform] = useState('desktop');

  // Push Notification State
  const [pushPermission, setPushPermission] = useState('unknown'); // 'granted', 'denied', 'default', 'unsupported'
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setPreferences({
        time_format: user.time_format || '24',
        date_format: user.date_format || 'DD/MM/YYYY'
      });
      // Load email settings and notification preferences
      fetchEmailSettings();
      fetchNotificationPreferences();
      // Check GPS permission status
      checkGPSPermission();
      // Check Push notification status
      checkPushPermission();
      // Load GPS tracking preference from localStorage
      const savedGpsTracking = localStorage.getItem(`gps_tracking_enabled_${user.id}`);
      if (savedGpsTracking !== null) {
        setGpsTrackingEnabled(savedGpsTracking === 'true');
      }
      // Detect platform
      const userAgent = navigator.userAgent.toLowerCase();
      if (/iphone|ipad|ipod/.test(userAgent)) {
        setDetectedPlatform('ios');
      } else if (userAgent.includes('android')) {
        setDetectedPlatform('android');
      } else {
        setDetectedPlatform('desktop');
      }
    }
  }, [user]);

  // Check current Push notification permission status
  const checkPushPermission = async () => {
    if (!isPushSupported()) {
      setPushPermission('unsupported');
      return;
    }

    const permission = getNotificationPermission();
    setPushPermission(permission);

    // Check if already subscribed
    if (permission === 'granted' && 'serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.getRegistration('/');
        if (registration) {
          const subscription = await registration.pushManager.getSubscription();
          setPushSubscribed(!!subscription);
        }
      } catch (e) {
        console.error('Error checking push subscription:', e);
      }
    }
  };

  // Enable push notifications
  const enablePushNotifications = async () => {
    setPushLoading(true);
    try {
      // Request permission
      const permission = await requestNotificationPermission();
      setPushPermission(permission);

      if (permission === 'granted') {
        // Subscribe to push
        const subscription = await subscribeToPush(token);
        if (subscription) {
          setPushSubscribed(true);
          toast.success('Push notifications enabled!');
        } else {
          toast.error('Failed to subscribe to push notifications');
        }
      } else if (permission === 'denied') {
        toast.error('Push notification permission denied');
      }
    } catch (error) {
      console.error('Error enabling push:', error);
      toast.error('Failed to enable push notifications');
    } finally {
      setPushLoading(false);
    }
  };

  // Disable push notifications
  const disablePushNotifications = async () => {
    setPushLoading(true);
    try {
      await unsubscribeFromPush(token);
      setPushSubscribed(false);
      toast.success('Push notifications disabled');
    } catch (error) {
      console.error('Error disabling push:', error);
      toast.error('Failed to disable push notifications');
    } finally {
      setPushLoading(false);
    }
  };

  // Check current GPS permission status
  const checkGPSPermission = async () => {
    if (!navigator.geolocation) {
      setGpsPermission('denied');
      return;
    }

    // First try the Permissions API (may not work on iOS Safari)
    if ('permissions' in navigator) {
      try {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        
        // If permission is granted via Permissions API, we're done
        if (result.state === 'granted') {
          setGpsPermission('granted');
          result.onchange = () => setGpsPermission(result.state);
          return;
        }
        
        // If denied, set it and return
        if (result.state === 'denied') {
          setGpsPermission('denied');
          result.onchange = () => setGpsPermission(result.state);
          return;
        }
        
        // If prompt, leave as is
        setGpsPermission(result.state);
        result.onchange = () => setGpsPermission(result.state);
      } catch (error) {
        console.log('Permissions API not available, trying position check');
      }
    }

    // Fallback for iOS: Try to get position with a quick timeout
    // This is more reliable on iOS Safari
    try {
      await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          resolve, 
          reject, 
          { enableHighAccuracy: false, timeout: 3000, maximumAge: 60000 }
        );
      });
      setGpsPermission('granted');
    } catch (error) {
      if (error.code === 1) { // PERMISSION_DENIED
        setGpsPermission('denied');
      } else if (error.code === 2 || error.code === 3) { // POSITION_UNAVAILABLE or TIMEOUT
        // On iOS, timeout or unavailable doesn't mean denied
        // Keep as unknown/prompt to let user try again
        if (gpsPermission !== 'granted') {
          setGpsPermission('prompt');
        }
      }
    }
  };

  // Request GPS permission
  const requestGPSPermission = async () => {
    setGpsLoading(true);
    try {
      // Try with high accuracy first (better for actual use)
      await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 15000, // Longer timeout for iOS
          maximumAge: 0
        });
      });
      setGpsPermission('granted');
      toast.success('GPS location enabled successfully!');
    } catch (error) {
      // If high accuracy fails, try low accuracy (works better on some iOS devices)
      try {
        await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: false,
            timeout: 10000,
            maximumAge: 60000
          });
        });
        setGpsPermission('granted');
        toast.success('GPS location enabled successfully!');
      } catch (secondError) {
        if (secondError.code === 1) {
          setGpsPermission('denied');
          // Show instructions modal instead of just a toast
          setShowGPSInstructionsModal(true);
        } else if (secondError.code === 2) {
          // Position unavailable - GPS might be off on device
          toast.error('Location unavailable. Please check that GPS/Location is enabled on your device.');
          setShowGPSInstructionsModal(true);
        } else if (secondError.code === 3) {
          // Timeout - might be slow GPS
          toast.warning('Location request timed out. Please try again or check your GPS signal.');
        } else {
          toast.error('Failed to get location. Please try again.');
        }
      }
    } finally {
      setGpsLoading(false);
    }
  };

  // Open browser settings instructions modal
  const showGPSInstructions = () => {
    setShowGPSInstructionsModal(true);
  };

  // Toggle GPS tracking within the app
  const toggleGPSTracking = async (enabled) => {
    if (enabled && gpsPermission !== 'granted') {
      // If enabling and permission not granted, request it first
      await requestGPSPermission();
    }
    setGpsTrackingEnabled(enabled);
    // Save preference to localStorage
    if (user?.id) {
      localStorage.setItem(`gps_tracking_enabled_${user.id}`, enabled.toString());
    }
    if (enabled) {
      toast.success('GPS tracking enabled');
    } else {
      toast.info('GPS tracking disabled. Note: You won\'t be able to use geofencing features.');
    }
  };

  const fetchEmailSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/email`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailSettings(response.data);
    } catch (error) {
      console.error('Error fetching email settings:', error);
    }
  };

  const fetchNotificationPreferences = async () => {
    try {
      const response = await axios.get(`${API}/auth/notification-preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotificationPrefs(response.data);
    } catch (error) {
      console.error('Error fetching notification preferences:', error);
    }
  };

  const handleNotificationPrefsUpdate = async (e) => {
    e.preventDefault();
    setNotificationPrefsLoading(true);
    try {
      await axios.put(`${API}/auth/notification-preferences`, notificationPrefs, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Notification preferences updated successfully!');
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      toast.error(error.response?.data?.detail || 'Failed to update notification preferences');
    } finally {
      setNotificationPrefsLoading(false);
    }
  };

  const handleEmailSettingsUpdate = async (e) => {
    e.preventDefault();
    
    setEmailSettingsLoading(true);
    try {
      await axios.put(`${API}/settings/email`, emailSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Email settings updated successfully!');
    } catch (error) {
      console.error('Error updating email settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to update email settings');
    } finally {
      setEmailSettingsLoading(false);
    }
  };

  const handleEmailUpdate = async (e) => {
    e.preventDefault();
    
    if (!emailData.current_password) {
      toast.error('Please enter your current password');
      return;
    }

    if (emailData.new_email === user.email) {
      toast.error('New email is the same as current email');
      return;
    }

    setLoading(true);
    try {
      await axios.put(
        `${API}/auth/update-email`,
        emailData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Email updated successfully! Please login again.');
      setTimeout(() => {
        logout();
        navigate('/login');
      }, 2000);
    } catch (error) {
      console.error('Error updating email:', error);
      toast.error(error.response?.data?.detail || 'Failed to update email');
    } finally {
      setLoading(false);
    }
  };

  const handlePreferencesUpdate = async (e) => {
    e.preventDefault();
    
    setPreferencesLoading(true);
    try {
      const response = await axios.put(
        `${API}/auth/update-preferences`,
        preferences,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Update user in context with new preferences
      updateUser({ ...user, ...preferences });
      toast.success('Preferences updated successfully!');
    } catch (error) {
      console.error('Error updating preferences:', error);
      toast.error(error.response?.data?.detail || 'Failed to update preferences');
    } finally {
      setPreferencesLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-blue-50">
      <header className="bg-white border-b border-blue-200 sticky top-0 z-10 shadow-sm">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/driver')}
              variant="ghost"
              size="icon"
              className="text-gray-700 hover:text-black hover:bg-blue-50"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <img 
              src="/logo.jpg" 
              alt="Outsiders Travel" 
              className="h-10 w-10 object-contain"
            />
            <div>
              <h1 className="text-2xl font-bold text-black">Settings</h1>
              <p className="text-sm text-gray-600 mt-1">Manage your account</p>
            </div>
          </div>
          <Button
            onClick={logout}
            variant="ghost"
            className="text-gray-700 hover:text-black hover:bg-blue-50"
          >
            <LogOut className="mr-2 h-5 w-5" />
            Logout
          </Button>
        </div>
      </header>

      <div className="p-6 max-w-2xl mx-auto space-y-6">
        <div className="bg-white border border-blue-200 rounded-sm shadow-sm p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-black">Profile Information</h2>
              <p className="text-sm text-gray-600">View your account details</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-gray-700 text-sm mb-2 block">Name</Label>
              <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-sm text-gray-700">
                {user?.name}
              </div>
            </div>

            <div>
              <Label className="text-gray-700 text-sm mb-2 block">Current Email</Label>
              <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-sm text-gray-700">
                {user?.email}
              </div>
            </div>

            <div>
              <Label className="text-gray-700 text-sm mb-2 block">Role</Label>
              <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-sm">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                  Driver
                </span>
              </div>
            </div>

            {user?.phone && (
              <div>
                <Label className="text-gray-700 text-sm mb-2 block">Phone</Label>
                <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-sm text-gray-700">
                  {user.phone}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white border border-blue-200 rounded-sm shadow-sm p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center">
              <Clock className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-black">Date & Time Preferences</h2>
              <p className="text-sm text-gray-600">Customize how dates and times are displayed</p>
            </div>
          </div>

          <form onSubmit={handlePreferencesUpdate} className="space-y-5">
            <div>
              <Label htmlFor="time_format" className="text-gray-700 font-medium mb-2 block">
                Time Format
              </Label>
              <Select
                value={preferences.time_format}
                onValueChange={(value) => setPreferences({ ...preferences, time_format: value })}
              >
                <SelectTrigger className="h-11 rounded-sm border-blue-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="12">12-hour (2:30 PM)</SelectItem>
                  <SelectItem value="24">24-hour (14:30)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="date_format" className="text-gray-700 font-medium mb-2 block">
                Date Format
              </Label>
              <Select
                value={preferences.date_format}
                onValueChange={(value) => setPreferences({ ...preferences, date_format: value })}
              >
                <SelectTrigger className="h-11 rounded-sm border-blue-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MM/DD/YYYY">MM/DD/YYYY (12/10/2024)</SelectItem>
                  <SelectItem value="DD/MM/YYYY">DD/MM/YYYY (10/12/2024)</SelectItem>
                  <SelectItem value="YYYY-MM-DD">YYYY-MM-DD (2024-12-10)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              type="submit"
              disabled={preferencesLoading}
              className="w-full h-11 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-sm"
            >
              {preferencesLoading ? (
                <span className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Updating...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <Save className="mr-2 h-5 w-5" />
                  Save Preferences
                </span>
              )}
            </Button>
          </form>
        </div>

        {/* GPS Location Settings */}
        <div className="bg-white border border-blue-200 rounded-sm shadow-sm p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 bg-emerald-100 rounded-full flex items-center justify-center">
              <MapPin className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-black">GPS Location</h2>
              <p className="text-sm text-gray-600">Manage location permissions for ride tracking</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* GPS Tracking Toggle - App Level */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center gap-3">
                <Navigation className="h-5 w-5 text-emerald-600" />
                <div>
                  <p className="font-medium text-slate-800">GPS Tracking</p>
                  <p className="text-sm text-slate-500">Enable location tracking for rides</p>
                </div>
              </div>
              <Switch
                checked={gpsTrackingEnabled}
                onCheckedChange={toggleGPSTracking}
                disabled={gpsLoading}
                data-testid="gps-tracking-switch"
              />
            </div>

            {/* Browser Permission Status */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${
                  gpsPermission === 'granted' ? 'bg-emerald-500' :
                  gpsPermission === 'denied' ? 'bg-red-500' :
                  'bg-amber-500'
                }`} />
                <div>
                  <p className="font-medium text-slate-800">Browser Permission</p>
                  <p className="text-sm text-slate-500">
                    {gpsPermission === 'granted' && 'Location access allowed'}
                    {gpsPermission === 'denied' && 'Location access blocked'}
                    {gpsPermission === 'prompt' && 'Permission not yet requested'}
                    {gpsPermission === 'unknown' && 'Checking status...'}
                  </p>
                </div>
              </div>
              <Switch
                checked={gpsPermission === 'granted'}
                onCheckedChange={(checked) => {
                  if (checked) {
                    requestGPSPermission();
                  } else {
                    // Can't programmatically revoke - show instructions
                    showGPSInstructions();
                  }
                }}
                disabled={gpsLoading}
                data-testid="browser-permission-switch"
              />
            </div>

            {/* GPS Benefits */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-start gap-3 p-3 bg-emerald-50 rounded-lg">
                <Navigation className="h-5 w-5 text-emerald-600 mt-0.5" />
                <div>
                  <p className="font-medium text-emerald-800 text-sm">Accurate Tracking</p>
                  <p className="text-emerald-600 text-xs">Verify arrival at pickup/dropoff</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-800 text-sm">Privacy Protected</p>
                  <p className="text-blue-600 text-xs">Location used only while in app</p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            {gpsPermission === 'granted' && gpsTrackingEnabled ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <p className="text-sm text-emerald-800 flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  GPS is fully enabled. Your location will be used for geofencing during rides.
                </p>
              </div>
            ) : gpsPermission === 'denied' ? (
              <div className="space-y-3">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800 flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>GPS access is blocked by your browser. You need to allow it in your browser/device settings.</span>
                  </p>
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={showGPSInstructions}
                    className="flex-1 h-11 bg-amber-500 hover:bg-amber-600 text-white rounded-sm"
                  >
                    <MapPin className="mr-2 h-4 w-4" />
                    How to Enable GPS
                  </Button>
                  <Button
                    onClick={requestGPSPermission}
                    disabled={gpsLoading}
                    variant="outline"
                    className="flex-1 h-11 rounded-sm"
                  >
                    {gpsLoading ? 'Checking...' : 'Try Again'}
                  </Button>
                </div>
              </div>
            ) : gpsPermission === 'prompt' || gpsPermission === 'unknown' ? (
              <Button
                onClick={requestGPSPermission}
                disabled={gpsLoading}
                className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-sm"
              >
                {gpsLoading ? (
                  <span className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Requesting...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <MapPin className="mr-2 h-5 w-5" />
                    Enable GPS Location
                  </span>
                )}
              </Button>
            ) : !gpsTrackingEnabled ? (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <p className="text-sm text-slate-600 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  GPS tracking is disabled. Turn on the toggle above to enable it.
                </p>
              </div>
            ) : null}
          </div>
        </div>

        {/* Push Notification Settings */}
        <div className="bg-white border border-blue-200 rounded-sm shadow-sm p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
              <Smartphone className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-black">Push Notifications</h2>
              <p className="text-sm text-gray-600">Receive notifications even when the app is closed</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Current Push Status */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${
                  pushPermission === 'granted' && pushSubscribed ? 'bg-emerald-500' :
                  pushPermission === 'denied' ? 'bg-red-500' :
                  pushPermission === 'unsupported' ? 'bg-slate-400' :
                  'bg-amber-500'
                }`} />
                <div>
                  <p className="font-medium text-slate-800">Push Notification Status</p>
                  <p className="text-sm text-slate-500">
                    {pushPermission === 'granted' && pushSubscribed && 'Push notifications enabled'}
                    {pushPermission === 'granted' && !pushSubscribed && 'Permission granted but not subscribed'}
                    {pushPermission === 'denied' && 'Push notifications blocked'}
                    {pushPermission === 'default' && 'Permission not requested'}
                    {pushPermission === 'unsupported' && 'Not supported in this browser'}
                    {pushPermission === 'unknown' && 'Checking status...'}
                  </p>
                </div>
              </div>
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                pushPermission === 'granted' && pushSubscribed ? 'bg-emerald-100 text-emerald-700' :
                pushPermission === 'denied' ? 'bg-red-100 text-red-700' :
                pushPermission === 'unsupported' ? 'bg-slate-100 text-slate-600' :
                'bg-amber-100 text-amber-700'
              }`}>
                {pushPermission === 'granted' && pushSubscribed ? 'Active' :
                 pushPermission === 'denied' ? 'Blocked' : 
                 pushPermission === 'unsupported' ? 'N/A' : 'Inactive'}
              </div>
            </div>

            {/* Action Buttons */}
            {pushPermission === 'unsupported' ? (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <p className="text-sm text-slate-600 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Push notifications are not supported in this browser. Try using Chrome, Firefox, or Edge.
                </p>
              </div>
            ) : pushPermission === 'granted' && pushSubscribed ? (
              <div className="space-y-3">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <p className="text-sm text-emerald-800 flex items-center gap-2">
                    <Bell className="h-4 w-4" />
                    Push notifications are active. You'll receive alerts for upcoming rides.
                  </p>
                </div>
                <Button
                  onClick={disablePushNotifications}
                  disabled={pushLoading}
                  variant="outline"
                  className="w-full h-11 rounded-sm text-red-600 border-red-200 hover:bg-red-50"
                >
                  {pushLoading ? 'Disabling...' : 'Disable Push Notifications'}
                </Button>
              </div>
            ) : pushPermission === 'denied' ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800 flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>
                    Push notifications are blocked. To enable them, click the lock icon in your browser's address bar and allow notifications for this site.
                  </span>
                </p>
              </div>
            ) : (
              <Button
                onClick={enablePushNotifications}
                disabled={pushLoading}
                className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-sm"
              >
                {pushLoading ? (
                  <span className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Enabling...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <Bell className="mr-2 h-5 w-5" />
                    Enable Push Notifications
                  </span>
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Notification Settings */}
        <div className="bg-white border border-blue-200 rounded-sm shadow-sm p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 bg-amber-100 rounded-full flex items-center justify-center">
              <Bell className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-black">Notification Settings</h2>
              <p className="text-sm text-gray-600">Manage your ride notification preferences</p>
            </div>
          </div>

          <form onSubmit={handleNotificationPrefsUpdate} className="space-y-5">
            {/* 30 Minutes Before Ride Reminder */}
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">30-Minute Ride Reminder</p>
                    <p className="text-sm text-amber-600">Get notified 30 minutes before each scheduled ride</p>
                    <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                      <Bell className="h-3 w-3" />
                      Push notification + In-app notification
                    </p>
                  </div>
                </div>
                <Switch
                  checked={notificationPrefs.reminder_30min_before}
                  onCheckedChange={(checked) => setNotificationPrefs({ ...notificationPrefs, reminder_30min_before: checked })}
                  data-testid="reminder-30min-switch"
                />
              </div>
            </div>

            {/* Ride Assigned Notification */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-800">Ride Assigned</p>
                <p className="text-sm text-slate-500">Get notified when a new ride is assigned to you</p>
              </div>
              <Switch
                checked={notificationPrefs.ride_assigned}
                onCheckedChange={(checked) => setNotificationPrefs({ ...notificationPrefs, ride_assigned: checked })}
                data-testid="ride-assigned-switch"
              />
            </div>

            {/* Ride Status Changed Notification */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-800">Ride Status Changed</p>
                <p className="text-sm text-slate-500">Get notified when a ride status is updated</p>
              </div>
              <Switch
                checked={notificationPrefs.ride_status_changed}
                onCheckedChange={(checked) => setNotificationPrefs({ ...notificationPrefs, ride_status_changed: checked })}
                data-testid="ride-status-switch"
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Push notifications require browser permission. Make sure to allow notifications when prompted. The 30-minute reminder will be sent as a push notification to your device.
              </p>
            </div>

            <Button
              type="submit"
              disabled={notificationPrefsLoading}
              className="w-full h-11 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-sm"
            >
              {notificationPrefsLoading ? (
                <span className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Saving...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <Save className="mr-2 h-5 w-5" />
                  Save Notification Settings
                </span>
              )}
            </Button>
          </form>
        </div>

        <div className="bg-white border border-blue-200 rounded-sm shadow-sm p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 bg-sky-100 rounded-full flex items-center justify-center">
              <Mail className="h-6 w-6 text-sky-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-black">Change Email</h2>
              <p className="text-sm text-gray-600">Update your email address</p>
            </div>
          </div>

          <form onSubmit={handleEmailUpdate} className="space-y-5" data-testid="email-update-form">
            <div>
              <Label htmlFor="new_email" className="text-gray-700 font-medium mb-2 block">
                New Email Address
              </Label>
              <Input
                id="new_email"
                type="email"
                value={emailData.new_email}
                onChange={(e) => setEmailData({ ...emailData, new_email: e.target.value })}
                placeholder="newemail@example.com"
                required
                className="h-11 rounded-sm border-blue-200 focus:ring-2 focus:ring-sky-500"
                data-testid="new-email-input"
              />
            </div>

            <div>
              <Label htmlFor="current_password" className="text-gray-700 font-medium mb-2 block">
                Current Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  id="current_password"
                  type="password"
                  value={emailData.current_password}
                  onChange={(e) => setEmailData({ ...emailData, current_password: e.target.value })}
                  placeholder="Enter your current password"
                  required
                  className="h-11 rounded-sm pl-10 border-blue-200 focus:ring-2 focus:ring-sky-500"
                  data-testid="current-password-input"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Required to confirm your identity</p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> After changing your email, you will be logged out and need to sign in again with your new email.
              </p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-black hover:bg-blue-900 text-white font-medium rounded-sm"
              data-testid="update-email-button"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Updating...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <Save className="mr-2 h-5 w-5" />
                  Update Email
                </span>
              )}
            </Button>
          </form>
        </div>

        {/* Notification Preferences */}
        <div className="bg-white border border-amber-200 rounded-sm shadow-sm p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 bg-amber-100 rounded-full flex items-center justify-center">
              <Bell className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-black">Notification Preferences</h2>
              <p className="text-sm text-gray-600">Choose which notifications you want to receive</p>
            </div>
          </div>

          <form onSubmit={handleNotificationPrefsUpdate} className="space-y-6">
            {/* In-App Notifications */}
            <div>
              <h3 className="font-medium text-gray-800 mb-3">In-App Notifications</h3>
              <div className="space-y-3 pl-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationPrefs.ride_assigned}
                    onChange={(e) => setNotificationPrefs({ ...notificationPrefs, ride_assigned: e.target.checked })}
                    className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                  />
                  <span className="text-sm text-gray-700">When a ride is assigned to me</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationPrefs.ride_status_changed}
                    onChange={(e) => setNotificationPrefs({ ...notificationPrefs, ride_status_changed: e.target.checked })}
                    className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                  />
                  <span className="text-sm text-gray-700">When my ride status changes</span>
                </label>
              </div>
            </div>

            {/* Email Notifications */}
            <div>
              <h3 className="font-medium text-gray-800 mb-3">Email Notifications</h3>
              <div className="space-y-3 pl-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationPrefs.email_ride_assigned}
                    onChange={(e) => setNotificationPrefs({ ...notificationPrefs, email_ride_assigned: e.target.checked })}
                    className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                  />
                  <span className="text-sm text-gray-700">Email me when a ride is assigned to me</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationPrefs.email_ride_status_changed}
                    onChange={(e) => setNotificationPrefs({ ...notificationPrefs, email_ride_status_changed: e.target.checked })}
                    className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                  />
                  <span className="text-sm text-gray-700">Email me when my ride status changes</span>
                </label>
              </div>
            </div>

            {/* Push Notifications */}
            <div>
              <h3 className="font-medium text-gray-800 mb-3">Push Notifications (Browser)</h3>
              <div className="space-y-3 pl-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationPrefs.push_ride_assigned}
                    onChange={(e) => setNotificationPrefs({ ...notificationPrefs, push_ride_assigned: e.target.checked })}
                    className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                  />
                  <span className="text-sm text-gray-700">Push notification when a ride is assigned to me</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationPrefs.push_ride_status_changed}
                    onChange={(e) => setNotificationPrefs({ ...notificationPrefs, push_ride_status_changed: e.target.checked })}
                    className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                  />
                  <span className="text-sm text-gray-700">Push notification when my ride status changes</span>
                </label>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> Email notifications require email settings below. Push notifications require browser permission (prompted on login).
              </p>
            </div>

            <Button
              type="submit"
              disabled={notificationPrefsLoading}
              className="w-full h-11 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-sm"
            >
              {notificationPrefsLoading ? (
                <span className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Saving...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <Save className="mr-2 h-5 w-5" />
                  Save Notification Preferences
                </span>
              )}
            </Button>
          </form>
        </div>

        {/* Email Notification Settings */}
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
          <h2 className="text-xl font-semibold text-slate-900 mb-6 flex items-center gap-2">
            <Mail className="h-5 w-5" />
            My Email Notification Settings
          </h2>

          <form onSubmit={handleEmailSettingsUpdate} className="space-y-6">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-sm">
              <input
                type="checkbox"
                id="email_enabled"
                checked={emailSettings.enabled}
                onChange={(e) => setEmailSettings({ ...emailSettings, enabled: e.target.checked })}
                className="w-5 h-5 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500 cursor-pointer"
              />
              <label htmlFor="email_enabled" className="text-sm font-medium text-slate-700 cursor-pointer">
                Send ride notifications to my email
              </label>
            </div>

            {emailSettings.enabled && (
              <>
                {/* Gmail Help Text */}
                <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
                  <h3 className="text-sm font-semibold text-blue-900 mb-2">Gmail Setup:</h3>
                  <div className="text-sm text-blue-800 space-y-2">
                    <p><strong>Option 1: If your Gmail has 2-Factor Authentication (2FA):</strong></p>
                    <ol className="list-decimal list-inside space-y-1 ml-4">
                      <li>Go to: <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="underline">myaccount.google.com/apppasswords</a></li>
                      <li>Create App Password for "Mail"</li>
                      <li>Copy the 16-character code (remove spaces)</li>
                      <li>Paste in "Password" field below</li>
                    </ol>
                    <p className="mt-2"><strong>Option 2: If your Gmail does NOT have 2FA:</strong></p>
                    <p className="ml-4">Just enter your regular Gmail password</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Gmail Address */}
                  <div>
                    <Label htmlFor="smtp_user" className="text-slate-700 font-medium mb-2 block">
                      Your Gmail Address
                    </Label>
                    <Input
                      id="smtp_user"
                      type="email"
                      value={emailSettings.smtp_user}
                      onChange={(e) => setEmailSettings({ ...emailSettings, smtp_user: e.target.value, smtp_from: e.target.value })}
                      placeholder="your-email@gmail.com"
                      className="h-11 rounded-sm"
                      required
                    />
                  </div>

                  {/* Gmail Password */}
                  <div>
                    <Label htmlFor="smtp_password" className="text-slate-700 font-medium mb-2 block">
                      Gmail Password
                    </Label>
                    <Input
                      id="smtp_password"
                      type="password"
                      value={emailSettings.smtp_password}
                      onChange={(e) => setEmailSettings({ ...emailSettings, smtp_password: e.target.value })}
                      placeholder="Password or App Password"
                      className="h-11 rounded-sm"
                    />
                    <p className="text-xs text-slate-500 mt-1">App Password (if 2FA) or regular password</p>
                  </div>
                </div>
              </>
            )}

            <Button 
              type="submit" 
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm h-11 px-6"
              disabled={emailSettingsLoading}
            >
              {emailSettingsLoading ? (
                <span className="mr-2">Saving...</span>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Email Settings
                </>
              )}
            </Button>
          </form>
        </div>
      </div>

      {/* GPS Instructions Modal */}
      {showGPSInstructionsModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[85vh] overflow-y-auto shadow-xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-emerald-600" />
                  Enable GPS Location
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowGPSInstructionsModal(false)}
                  className="h-8 w-8 p-0"
                >
                  ×
                </Button>
              </div>
              
              <div className="space-y-4">
                {/* Platform-specific instructions */}
                {detectedPlatform === 'ios' && (
                  <div className="space-y-4">
                    <div className="bg-slate-100 rounded-lg p-3">
                      <p className="text-sm font-medium text-slate-700">Detected: iPhone/iPad</p>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">1</div>
                        <div>
                          <p className="font-medium text-slate-800">Open Settings app</p>
                          <p className="text-sm text-slate-600">Go to your iPhone Settings</p>
                        </div>
                      </div>
                      
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">2</div>
                        <div>
                          <p className="font-medium text-slate-800">Privacy & Security → Location Services</p>
                          <p className="text-sm text-slate-600">Turn ON Location Services</p>
                        </div>
                      </div>
                      
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">3</div>
                        <div>
                          <p className="font-medium text-slate-800">Find Safari (or your browser)</p>
                          <p className="text-sm text-slate-600">Set to "While Using the App"</p>
                        </div>
                      </div>
                      
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-emerald-500 text-white rounded-full flex items-center justify-center font-bold text-sm">4</div>
                        <div>
                          <p className="font-medium text-slate-800">Return here and tap "Try Again"</p>
                          <p className="text-sm text-slate-600">The app will request permission again</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {detectedPlatform === 'android' && (
                  <div className="space-y-4">
                    <div className="bg-slate-100 rounded-lg p-3">
                      <p className="text-sm font-medium text-slate-700">Detected: Android</p>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">1</div>
                        <div>
                          <p className="font-medium text-slate-800">Open Settings app</p>
                          <p className="text-sm text-slate-600">Go to your phone Settings</p>
                        </div>
                      </div>
                      
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">2</div>
                        <div>
                          <p className="font-medium text-slate-800">Location</p>
                          <p className="text-sm text-slate-600">Turn ON "Use location"</p>
                        </div>
                      </div>
                      
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">3</div>
                        <div>
                          <p className="font-medium text-slate-800">In Chrome: tap ⋮ → Settings → Site settings → Location</p>
                          <p className="text-sm text-slate-600">Set to "Ask first" or find this site and allow</p>
                        </div>
                      </div>
                      
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-emerald-500 text-white rounded-full flex items-center justify-center font-bold text-sm">4</div>
                        <div>
                          <p className="font-medium text-slate-800">Return here and tap "Try Again"</p>
                          <p className="text-sm text-slate-600">The app will request permission again</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {detectedPlatform === 'desktop' && (
                  <div className="space-y-4">
                    <div className="bg-slate-100 rounded-lg p-3">
                      <p className="text-sm font-medium text-slate-700">Desktop Browser</p>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">1</div>
                        <div>
                          <p className="font-medium text-slate-800">Click the lock icon 🔒 in address bar</p>
                          <p className="text-sm text-slate-600">It's at the left side of the URL</p>
                        </div>
                      </div>
                      
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">2</div>
                        <div>
                          <p className="font-medium text-slate-800">Find "Location" in permissions</p>
                          <p className="text-sm text-slate-600">Change from "Block" to "Allow"</p>
                        </div>
                      </div>
                      
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-emerald-500 text-white rounded-full flex items-center justify-center font-bold text-sm">3</div>
                        <div>
                          <p className="font-medium text-slate-800">Click "Try Again" below</p>
                          <p className="text-sm text-slate-600">The app will request permission again</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="pt-2 space-y-2">
                  <Button
                    onClick={() => {
                      setShowGPSInstructionsModal(false);
                      requestGPSPermission();
                    }}
                    className="w-full h-11 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg"
                  >
                    <MapPin className="h-4 w-4 mr-2" />
                    I've Done It - Try Again
                  </Button>
                  <Button
                    onClick={() => setShowGPSInstructionsModal(false)}
                    variant="outline"
                    className="w-full h-10 rounded-lg"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
